import { Route, Routes, matchPath, useLocation } from "react-router-dom"
import MainLayout from "components/layout"
import NotFound from "pages/404"
import routes from "routes"
import ProtectedRoute from "protectedRoute"
import { getAccessTokenData, checkTokenValid } from "utils/token-helper"
import { useEffect, useState } from "react"
import { CircularProgress } from "@mui/material"
import { loginWithAzureB2C, getAccessToken } from "utils/external-login-helper"
import { LoggingHelper } from "utils/logging-helper"
import { LoggingLevel } from "shared/constants/logging-level"
import FileSaver from "file-saver"
import { AppLoadingContainer } from "components/common-styles"
import { rootStore } from "stores/root-store"
import { storageName } from "shared/constants/storage-constant"
import * as StorageHelper from "utils/storage-helper"
import { SettingConstant } from "shared/constants/setting-constant"
import * as endPoint from "shared/constants/endpoint"
import MaintenanceScreen from "pages/maintenance-screen"
import { generalBusiness } from "businesses/general-business"
import { setUserContext, trackPageView } from "utils/analytics"

// One page_view per navigation, reported by route template so order numbers etc. never reach GA
const PageViewTracker = () => {
  const { pathname } = useLocation()
  useEffect(() => {
    const route = routes.find(r => matchPath(r.path, pathname))
    trackPageView(route ? route.path : "/404", route ? route.title : "Not found")
  }, [pathname])
  return null
}

export const App = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [maintenanceMode, setMaintenanceMode] = useState(false)

  const includeCustomFont = () => {
    if (SettingConstant.CUSTOM_FONT_URL) {
      const head = document.head
      const link = document.createElement("link")

      link.type = "text/css"
      link.rel = "stylesheet"
      link.href = SettingConstant.CUSTOM_FONT_URL

      head.appendChild(link)
    }
  }

  useEffect(() => {
    let params = {},
      queryString = location.search,
      regex = /([^&=]+)=([^&]*)/g,
      m
    while ((m = regex.exec(queryString))) {
      params[decodeURIComponent(m[1])] = decodeURIComponent(m[2])
    }

    LoggingHelper.writeLog("App", "loading", `queryString: ${queryString}, params: ${params ? JSON.stringify(params) : ""}`)

    if (params["code"] || queryString.includes("unauthorized")) {
      handleNormalMode(params, queryString)
    } else {
      generalBusiness
        .getEnvironmentVariables()
        .then(res => {
          const clientSecret = res.clientSecret ?? SettingConstant.CLIENT_SECRET_VALUE
          StorageHelper.setStorage(SettingConstant.CLIENT_SECRET, clientSecret)
          if (res.maintenanceMode) {
            setMaintenanceMode(true)
          } else {
            handleNormalMode(params, queryString)
          }
        })
        .catch(() => handleNormalMode(params, queryString))
    }
  }, [])

  const handleNormalMode = (params, queryString) => {
    const code = params["code"]
    if (queryString.includes("unauthorized")) {
      loginWithAzureB2C()
    } else if (code) {
      getAccessToken(code)
    } else {
      checkTokenValid()
        .then(valid => {
          if (valid) {
            const data = getAccessTokenData()
            if (!data) {
              LoggingHelper.writeLog("App", "loading", `failed to get access token data`, LoggingLevel.Warning)
              loginWithAzureB2C()
              return
            }

            const activeCompany = StorageHelper.getStorage(storageName.activeCompany)
            const email = data.emails && data.emails.length > 0 ? data.emails[0] : ""

            rootStore.setIsAuthenticated(true)

            StorageHelper.setStorage(storageName.user, data.name)
            rootStore.setUserName(data.name)
            rootStore.setUserEmail(email)

            // Customers are always their own company; only internal users keep a previously chosen one
            if (!activeCompany || data.jobTitle != "Internal User") StorageHelper.setStorage(storageName.activeCompany, data.jobTitle)

            const isSuperUser = data.jobTitle == "Internal User"
            rootStore.setIsSuperUser(isSuperUser)
            if (!isSuperUser) rootStore.setCompany(data.jobTitle)
            else if (activeCompany) rootStore.setCompany(activeCompany)
            // An internal user who hasn't picked a customer yet has "Internal User" stored, which isn't a client
            const clientCode = StorageHelper.getStorage(storageName.activeCompany)
            setUserContext({ companyCode: clientCode === "Internal User" ? "" : clientCode, isInternal: isSuperUser })

            rootStore.loadInitialData().then(() => setIsLoading(false))
          } else {
            loginWithAzureB2C()
          }
        })
        .catch(error => LoggingHelper.writeLog("App", "loading", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error))
    }

    //includeCustomFont()
  }

  const handleDocumentKeyup = e => {
    /* If DEBUG=true ctrl+alt+c serializes logging information and store data */
    if (e.keyCode === 67 && e.ctrlKey && e.altKey) {
      const log = LoggingHelper.getDebugLog()

      FileSaver.saveAs(
        new Blob(
          [log].map(o => JSON.stringify(o, null, 2)),
          {
            type: "application/json",
          },
        ),
        "express-logging.json",
      )
      FileSaver.saveAs(
        new Blob(
          [rootStore].map(o => JSON.stringify(o, null, 2)),
          {
            type: "application/json",
          },
        ),
        "express-state.json",
      )

      LoggingHelper.resetDebugLog()
    }
  }

  useEffect(() => {
    document.body.addEventListener("keyup", handleDocumentKeyup)

    const interval = setInterval(() => {
      rootStore.getNotifications()
    }, 5 * 60000)

    return () => {
      document.body.removeEventListener("keyup", handleDocumentKeyup)

      if (interval) clearInterval(interval)
    }
  }, [])

  if (maintenanceMode) return <MaintenanceScreen />

  if (isLoading) {
    return (
      <AppLoadingContainer>
        <CircularProgress size={64} />
      </AppLoadingContainer>
    )
  }

  return (
    <MainLayout>
      <PageViewTracker />
      <Routes>
        {routes.map(props => (
          <Route
            key={props.name}
            path={props.path}
            name={props.name}
            element={
              <ProtectedRoute>
                <props.component />
              </ProtectedRoute>
            }
          ></Route>
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </MainLayout>
  )
}
